/**
 * Brand Forgot Password Component
 * Allows brand partners to request a password reset email
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import '../assets/css/brand-portal.css';

// Use environment variable or default based on environment
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (process.env.NODE_ENV === 'production') {
    return 'https://yemberzal-app-28481d1d0b39.herokuapp.com';
  }
  return 'http://localhost:8000';
};

const API_URL = getApiUrl();

function BrandForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/brand/request-password-reset/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitted(true);
      } else {
        setError(data.error || 'Failed to send reset email. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="brand-auth-page">
        <div className="brand-auth-container">
          <div className="brand-auth-card">
            <div className="brand-auth-header">
              <div style={{
                width: 64,
                height: 64,
                background: 'rgba(16, 185, 129, 0.1)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </div>
              <h1 className="brand-auth-title">Check Your Email</h1>
              <p className="brand-auth-subtitle">
                If an account with <strong>{email}</strong> exists, we've sent a password reset link. Please check your inbox and spam folder.
              </p>
            </div>

            <Link to="/brand-portal/login" className="brand-btn brand-btn-primary" style={{ textDecoration: 'none' }}>
              Back to Login
            </Link>

            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <button
                onClick={() => { setSubmitted(false); setEmail(''); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#666',
                  cursor: 'pointer',
                  fontSize: 14,
                  textDecoration: 'underline',
                  fontFamily: 'inherit',
                }}
              >
                Try a different email
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="brand-auth-page">
      <div className="brand-auth-container">
        <div className="brand-auth-card">
          <div className="brand-auth-header">
            <Link to="/">
              <img
                src={require('../assets/images/Y.png')}
                alt="Yemberzal"
                className="brand-auth-logo"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = '/default-image.avif';
                }}
              />
            </Link>
            <h1 className="brand-auth-title">Reset Password</h1>
            <p className="brand-auth-subtitle">
              Enter your email address and we'll send you a link to reset your password
            </p>
          </div>

          {error && (
            <div className="brand-alert brand-alert-error">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="brand-form-group">
              <label htmlFor="email" className="brand-form-label">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                className="brand-form-input"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                required
                autoComplete="email"
                autoFocus
              />
            </div>

            <button
              type="submit"
              className="brand-btn brand-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="brand-spinner" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </form>

          <div className="brand-auth-footer">
            <Link to="/brand-portal/login" style={{ color: 'inherit', textDecoration: 'none' }}>
              &larr; Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BrandForgotPassword;
